import { MediaSubTypeEnum } from '@arhs-fleetback/types/entities/media';
import { all, call, put, select, take, takeLatest, takeEvery } from 'redux-saga/effects';

import { restClient } from 'config/store/configureStore';
import { SELECT_DEALER } from 'constants/ActionTypes';
import { MEDIAS_API_UPLOAD } from 'constants/ApiConstants';
import { APPLICATION_IS_READY } from 'modules/app/actionTypes';
import { ENTITY_TYPE } from 'modules/app/constants';
import { applicationIsReady } from 'modules/app/selectors';
import { SELECT_GROUP } from 'modules/groups/actionTypes';
import * as actions from 'modules/mediaLibrary/actions';
import * as actionTypes from 'modules/mediaLibrary/actionTypes';
import { getFetchParams, getMediaMode, isMediaLibraryLocation } from 'modules/mediaLibrary/selector';

import * as mediasAction from 'modules/medias/actions';
import * as mediaActionTypes from 'modules/medias/actionTypes';
import { LOCATION_CHANGE } from 'modules/router/actionTypes';
import { createNotification } from 'modules/toast-notifications/actions';

function* fetchMediaLibrary({ payload, meta }) {
  const { request } = payload;
  try {
    const { data } = yield call(restClient.request, request);
    yield put(actions.fetchMediaLibrarySuccess(data, meta));
  } catch (error) {
    yield put(actions.fetchMediaLibraryFailure(error, meta));
  }
}

function* refreshMediaLibrary() {
  const isReady = yield select(applicationIsReady);
  if (!isReady) {
    yield take(APPLICATION_IS_READY);
  }
  const isValidLocation = yield select(isMediaLibraryLocation);
  if (isValidLocation) {
    const source = yield select(getMediaMode);
    const params = yield select(getFetchParams);

    yield put(actions.fetchMediaLibrary(params, { source: `dashboard-${source}` }));
  }
}

function* updateMediaLibrary({ payload }) {
  const { request } = payload;
  try {
    const { data } = yield call(restClient.request, request);
    yield put(actions.updateMediaLibrarySuccess(data));
  } catch (error) {
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'http.error.message',
          defaultMessage: 'Oops! Something went wrong.',
        },
      })
    );
    yield put(actions.updateMediaLibraryFailure(error));
  }
}

function* deleteMediaLibrary({ payload, mediaLibraryId }) {
  const { request } = payload;
  try {
    yield call(restClient.request, request);
    yield put(actions.deleteMediaLibrarySuccess(mediaLibraryId));
  } catch (error) {
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'http.error.message',
          defaultMessage: 'Oops! Something went wrong.',
        },
      })
    );
    yield put(actions.deleteMediaLibraryFailure(error));
  }
}

export const takeUploadItemsFn =
  (fileUUID, waitedTypes) =>
    ({ type, meta }) =>
      waitedTypes.includes(type) && meta.fileUUID === fileUUID;

function* uploadItem(item, params, meta) {
  const metaAction = { ...meta, fileUUID: item.id };
  yield put(
    mediasAction.uploadInTwoSteps(
      {
        url: MEDIAS_API_UPLOAD,
        file: item.file,
        params: {
          ...params,
          entityType: ENTITY_TYPE.MEDIA_LIBRARY,
          subType: MediaSubTypeEnum.MEDIA,
          type: item.mediaType,
        },
      },
      metaAction
    )
  );

  const result = yield take(
    takeUploadItemsFn(item.id, [mediaActionTypes.UPLOAD_TWO_STEPS_SUCCESS, mediaActionTypes.UPLOAD_TWO_STEPS_FAILURE])
  );

  if (result.type === mediaActionTypes.UPLOAD_TWO_STEPS_SUCCESS) {
    const { filename, type, uploadId } = result.payload;
    const itemToAdd = {
      favorite: item.isFavorite,
      name: item.name,
      media: { filename, type, uploadId },
      ...params,
    };

    yield put(actions.addItem({ data: itemToAdd, meta: metaAction }));
    const response = yield take(
      takeUploadItemsFn(item.id, [
        actionTypes.ADD_ITEM_FROM_MEDIA_LIBRARY_SUCCESS,
        actionTypes.ADD_ITEM_FROM_MEDIA_LIBRARY_FAILURE,
      ])
    );

    return response.type === actionTypes.ADD_ITEM_FROM_MEDIA_LIBRARY_SUCCESS
      ? { ...response.payload, isUploaded: true }
      : { ...item, isUploaded: false };
  }
  return { ...item, isUploaded: false };
}

function* uploadItems({ payload, meta }) {
  const { items, params } = payload;

  const uploadedMedias = yield all(items.map(item => call(uploadItem, item, params, meta)));
  const hasError = uploadedMedias.some(item => !item.isUploaded);
  if (!hasError) {
    yield put(actions.uploadItemSuccess(uploadedMedias, meta));
  } else {
    yield put(actions.uploadItemFailure(uploadedMedias, meta));
  }
}

function* addItem({ payload, meta }) {
  const { request } = payload;
  try {
    const { data } = yield call(restClient.request, request);
    yield put(actions.addItemSuccess(data, meta));
  } catch (error) {
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'http.error.message',
          defaultMessage: 'Oops! Something went wrong.',
        },
      })
    );
    yield put(actions.addItemFailure(error, meta));
  }
}

export default function* mediaLibrarySagas() {
  yield takeLatest(
    [
      SELECT_DEALER,
      SELECT_GROUP,
      actionTypes.SET_MEDIA_LIBRARY_FILTER,
      actionTypes.REMOVE_MEDIA_LIBRARY_FILTER,
      actionTypes.CLEAR_MEDIA_LIBRARY_FILTERS,
      LOCATION_CHANGE,
    ],
    refreshMediaLibrary
  );
  yield takeLatest(actionTypes.FETCH_MEDIA_LIBRARY, fetchMediaLibrary);
  yield takeLatest(actionTypes.UPDATE_MEDIA_LIBRARY, updateMediaLibrary);
  yield takeLatest(actionTypes.DELETE_MEDIA_LIBRARY, deleteMediaLibrary);
  yield takeLatest(actionTypes.UPLOAD_MEDIA_LIBRARY_ITEMS, uploadItems);
  yield takeEvery(actionTypes.ADD_ITEM_TO_MEDIA_LIBRARY, addItem);
}
